import { useForm, useController } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { usePage } from '@inertiajs/react'
import dayjs from 'dayjs'

import useMutation from '@hooks/useGQLMutation'
import useQuery from '@hooks/useQuery'
import { logContactMutation } from './mutations'
import { upcomingEventsQuery } from './queries'
import { useAlertsStore } from '@stores/alertsStore'
import { FormDialog } from '@components/molecules/FormDialog'

import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import LabelledSelect from '@atoms/form/LabelledSelect'
import { Textarea } from '@atoms/form/Textarea'
import TagsSelect from '@components/molecules/TagsSelect'
import type { LogContactDialogState } from '../../types/dialogTypes'
import type { SharedInertiaProps } from '../../pages/GroupShow'

const t = scopedTranslation('components.log_contact_dialog')
const tAttributes = scopedTranslation('attributes.contact')

const ALERTS_PATH = 'logContact' as const

const schema = yup.object({
  contactMethod: yup
    .string()
    .label(tAttributes('contact_method'))
    .required(i18nValidation('required')),
  contactStatus: yup
    .string()
    .label(tAttributes('contact_status'))
    .required(i18nValidation('required')),
  note: yup
    .string(),
  tags: yup
    .array()
    .of(
      yup.object({
        value: yup.string().required(),
        label: yup.string().required(),
      })
    ),
  eventId: yup
    .string()
}).required()

const getContactMethodOptions = () => [
  { value: 'phone_call', label: t('form.options.contact_methods.phone_call') },
  { value: 'email', label: t('form.options.contact_methods.email') },
  { value: 'text', label: t('form.options.contact_methods.text') },
  { value: 'face_to_face', label: t('form.options.contact_methods.face_to_face') }
]

const getContactStatusOptions = () => [
  { value: 'answered', label: t('form.options.contact_statuses.answered') },
  { value: 'bad_info', label: t('form.options.contact_statuses.bad_info') },
  { value: 'inaccessible', label: t('form.options.contact_statuses.inaccessible') },
  { value: 'left_message', label: t('form.options.contact_statuses.left_message') },
  { value: 'meaningful_interaction', label: t('form.options.contact_statuses.meaningful_interaction') },
  { value: 'not_interested', label: t('form.options.contact_statuses.not_interested') },
  { value: 'no_answer', label: t('form.options.contact_statuses.no_answer') },
  { value: 'refused', label: t('form.options.contact_statuses.refused') },
  { value: 'send_information', label: t('form.options.contact_statuses.send_information') },
  { value: 'other', label: t('form.options.contact_statuses.other') }
]

type FormData = {
  contactMethod: string
  contactStatus: string
  note?: string
  tags?: Array<{ value: string; label: string }>
  eventId?: string
}

type LogContactDialogProps = {
  onClose: () => void
  dialogState: LogContactDialogState
}

type UpcomingEventsData = {
  events: {
    nodes: Array<{
      id: string
      name: string
      startTime: string
    }>
  }
}

export function LogContactDialog({
  onClose,
  dialogState,
}: LogContactDialogProps) {
  const { dialogData } = dialogState
  const supporterId = dialogData.id
  const supporterName = `${dialogData.firstName || ''} ${dialogData.lastName || ''}`.trim()
  const groupId = dialogData.groupId
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const { props: { permissions } } = usePage<SharedInertiaProps>()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  })

  const logContactMutationHook = useMutation(logContactMutation)
  const upcomingEventsQueryHook = useQuery<UpcomingEventsData>(
    {
      key: 'upcomingEvents',
      query: upcomingEventsQuery,
      variables: {}
    }
  )

  const events = upcomingEventsQueryHook.data?.events?.nodes?.map((event) => {
    const eventDate = dayjs(event.startTime).format('D MMMM YYYY')
    return {
      value: event.id,
      label: `${event.name} (${eventDate})`
    }
  }) || []

  const onSubmit = (data: FormData) => {
    clearAlerts(ALERTS_PATH)

    const mutationData = {
      supporterId,
      groupId,
      contactMethod: data.contactMethod,
      contactStatus: data.contactStatus,
      note: data.note,
      tags: data.tags?.map((tag) => tag.value),
      eventId: data.eventId,
    }

    logContactMutationHook.mutate(mutationData, {
      onSuccess: (response) => {
        if (response?.logContact?.success) {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [{
              type: 'success',
              details: {
                title: t('alerts.success.title')
              }
            }]
          })
          reset()
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: response?.logContact?.errors?.map(error => ({
              type: 'error',
              details: {
                title: error.message
              }
            })) || []
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [{
            type: 'error',
            details: {
              title: t('errors.log_failed')
            }
          }]
        })
      }
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  return (
    <FormDialog.Root 
      open={true}
      onOpenChange={handleOpenChange}
      testId="log-contact-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <LabelledSelect
            {...useController({ name: 'contactMethod', control }).field}
            label={tAttributes('contact_method')}
            placeholder={t('form.placeholders.contact_method')}
            options={getContactMethodOptions()}
            errors={errors}
            required={true}
          />

          <LabelledSelect
            {...useController({ name: 'contactStatus', control }).field}
            label={tAttributes('contact_status')}
            placeholder={t('form.placeholders.contact_status')}
            options={getContactStatusOptions()}
            errors={errors}
            required={true}
          />

          {/* Wrapper div needed because TagsSelect returns a fragment (label + select) 
              and FormDialog.Body applies tw-space-y-4 which would create gaps between them */}
          {permissions?.showAllTags && (
            <div>
              <TagsSelect
                  control={control}
                  name="tags"
                  createOptionPrefix={t('form.labels.create_tag')}
                  searchPlaceholder={t('form.placeholders.tags_search')}
              />
            </div>
          )}

          <LabelledSelect
            {...useController({ name: 'eventId', control }).field}
            label={t('form.labels.event')}
            description={t('form.descriptions.event_rsvp', { name: supporterName })}
            placeholder={t('form.placeholders.no_rsvp_confirmed')}
            options={events}
            errors={errors}
            disabled={upcomingEventsQueryHook.isLoading}
            />

          <Textarea
            label={tAttributes('note')}
            errors={errors}
            placeholder={t('form.placeholders.note')}
            rows={4}
            {...register('note')}
          />
        </FormDialog.Body>
        <FormDialog.Footer 
          submitText={t('form.log_contact')} 
          isLoading={logContactMutationHook.isLoading}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
